// Where a thing found by search opens.
//
// A person or a store can be looked at on many pages, so picking one opens the second
// step, "Where do you want to look?", at an address of its own: Back returns to it and
// it can be reopened. Everything else has one obvious page and goes straight there.
import type { ThingKind } from '../services/searchApi';
import { buildDrillUrl } from '../utils/drilldown';
import { customDashboardPath } from './dashboards';

export const SEARCH_PATH = '/search';

/** The kinds that have a second step. */
export type LookKind = 'person' | 'store';
export const isLookKind = (value: unknown): value is LookKind => value === 'person' || value === 'store';

/**
 * The second step for a person (by employee code) or a store (by its row id). The id
 * travels in the query of one fixed path, so analytics names the page '/search' and
 * never sees who or what it is about (lib/clientAnalytics).
 */
export const thingPath = (kind: LookKind, id: string): string =>
  `${SEARCH_PATH}?${new URLSearchParams({ kind, id }).toString()}`;

const TASK_RESPONSE = '/reports/task-response';
const TASKS = '/master-management/tasks';
const MODULE_REPORT = '/reports/content-modules';
const MODULES = '/lms/content';
const JOURNEYS = '/lms/journeys';
const JOURNEY_REPORT = '/reports/learning-journeys';
const SESSIONS = '/lms/sessions';
const OFFLINE_TRAININGS = '/grow/offline-trainings';

/**
 * The report page a team-built report is a tab of, by the source key its id starts
 * with. The page does not read a tab from its address (components/CustomReportPanel
 * keeps the open tab in state), so the report's page is as close as a link gets.
 * A report tied to one task's page is not in search; one with no page here cannot be
 * opened from a link at all.
 */
const REPORT_PAGES = new Map<string, string>([
  ['daily-attendance', '/reports/daily-attendance'],
  ['random-attendance', '/reports/random-attendance'],
  ['grooming', '/reports/grooming'],
  ['mood', '/reports/mood'],
  ['store-visits', '/reports/store-visits'],
  ['offline-training', '/grow/offline-trainings'],
]);

/**
 * The address a found thing opens, or null when there is nowhere to open it (it is
 * then not listed).
 *
 * `holds` says whether this person holds a page, by the same menu rules page search
 * lists pages with: search must not hand out a page the top bar would not show. It
 * picks between two pages for a task, and a team-built report is listed only when its
 * page is held.
 *
 * Training is checked shelf by shelf. The server lets anyone who holds any one of six
 * training pages search all four shelves, so holding one says nothing about the rest:
 * a person with only a learning report would otherwise be sent to Grow's set-up pages.
 * A shelf whose pages they hold none of has no address, and its rows are left out.
 *
 * `name` is the thing's own name. The Tasks, Learning Journeys and Offline Trainings
 * pages list everything, so they are opened with it in their search box (?q=, read by
 * hooks/useLinkedSearch): the page then shows the thing picked, not its whole list.
 * Products has no search box and opens as it is.
 */
export function thingDestination(kind: ThingKind, id: string, holds: (path: string) => boolean, name?: string): string | null {
  switch (kind) {
    case 'person':
    case 'store':
      return thingPath(kind, id);
    case 'task':
      return holds(TASK_RESPONSE) ? `${TASK_RESPONSE}/${encodeURIComponent(id)}` : buildDrillUrl(TASKS, { q: name });
    case 'training': {
      const at = id.indexOf(':');
      const shelf = id.slice(0, at);
      const own = id.slice(at + 1);
      if (at < 1 || !own) return null;
      if (shelf === 'module') {
        if (holds(MODULE_REPORT)) return `${MODULE_REPORT}?${new URLSearchParams({ module: own }).toString()}`;
        return holds(MODULES) ? MODULES : null;
      }
      // Its learners are a page under Sessions, which has no menu row of its own.
      if (shelf === 'session') return holds(SESSIONS) ? `${SESSIONS}/${encodeURIComponent(own)}/learners` : null;
      if (shelf === 'journey') {
        if (holds(JOURNEYS)) return buildDrillUrl(JOURNEYS, { q: name });
        // The report reads nothing from its address, so it opens on every journey.
        return holds(JOURNEY_REPORT) ? JOURNEY_REPORT : null;
      }
      if (shelf === 'offline') return holds(OFFLINE_TRAININGS) ? buildDrillUrl(OFFLINE_TRAININGS, { q: name }) : null;
      return null;
    }
    case 'product':
      return '/master-management/products';
    case 'dashboard':
      return customDashboardPath(encodeURIComponent(id));
    case 'report': {
      const page = REPORT_PAGES.get(id.slice(0, Math.max(id.lastIndexOf(':'), 0)));
      return page && holds(page) ? page : null;
    }
    default:
      return null;
  }
}
